import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import DashboardLayout from "../../Layout/DashboardLayout";
import ConfigTable from "../../Common/Tables/ConfigTable";
import ConfirmDialog, { showConfirmDialog } from "../../Common/ConfirmDialog";
import {
  Button,
  Icon,
  PageHeading,
  hasPrivilege,
  PRIVILEGES,
} from "../../Common/UIComponents";
import {
  fetchRoles,
  deleteRole,
  role_UpdateSortOrder,
} from "../../API_Service/Role_Slice";

// ─────────────────────────────────────────────
// STATS ROW
// ─────────────────────────────────────────────

const StatsRow = ({ roles }) => {
  const total = roles.length;
  const active = roles.filter((r) => r.StatusCode === "AC").length;
  const inactive = total - active;

  const stats = [
    {
      label: "Total Roles",
      value: total,
      icon: "shield_person",
      iconBg: "bg-[#005b60]/10",
      iconColor: "text-[#005b60]",
    },
    {
      label: "Active",
      value: active,
      icon: "verified",
      iconBg: "bg-[#006fa8]/10",
      iconColor: "text-[#006fa8]",
    },
    {
      label: "Inactive",
      value: inactive,
      icon: "block",
      iconBg: "bg-[#dc2626]/10",
      iconColor: "text-[#dc2626]",
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
      {stats.map((s) => (
        <div
          key={s.label}
          className="bg-white p-3.5 rounded-xl shadow-[0px_20px_40px_rgba(24,28,29,0.06)] border border-white/50"
        >
          <p className="text-[#3e494a] text-xs font-bold uppercase tracking-widest mb-1.5">
            {s.label}
          </p>
          <div className="flex items-center gap-3">
            <div className={`p-2 ${s.iconBg} rounded-lg ${s.iconColor}`}>
              <span className="material-symbols-outlined text-[20px]">
                {s.icon}
              </span>
            </div>
            <h3 className="text-2xl font-black text-[#181c1d]">{s.value}</h3>
          </div>
        </div>
      ))}
    </div>
  );
};

const EXPORT_COLUMNS = [
  { key: "RoleName", title: "Role Name" },
  { key: "RoleCode", title: "Role Code" },
  { key: "Description", title: "Description" },
];

const RoleManagementPage = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const companyId = user?.CompanyId || localStorage.getItem("companyId");
  const roleState = useSelector((state) => state.role) || {};
  const { loading = false } = roleState;
  const [roles, setRoles] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  const [searchTerm, setSearchTerm] = useState("");
  const [editSuccess, setEditSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [roleRows, setRoleRows] = useState([]);
  const [isSavingOrder, setIsSavingOrder] = useState(false);

  const confirmDialogRef = useRef();

  useEffect(() => {
    if (companyId) {
      dispatch(fetchRoles(companyId)).then((res) => {
        const data = res?.payload ?? [];
        setRoles(Array.isArray(data) ? data : []);
      });
    }
  }, [companyId, dispatch]);

  useEffect(() => {
    if (location.state?.refresh) {
      dispatch(fetchRoles(companyId)).then((res) => {
        const data = res?.payload ?? [];
        setRoles(Array.isArray(data) ? data : []);
      });
      if (location.state?.editSuccess) {
        setSuccessMsg(
          location.state?.isNew
            ? "Role Saved successfully."
            : "Role Updated successfully.",
        );
        setEditSuccess(true);
        setTimeout(() => setEditSuccess(false), 3000);
      }
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.refresh]);

  // ── Rows (kept sorted by status then SortOrder so drag handles stay meaningful) ──
  const rows = useMemo(
    () =>
      [...roles].sort((a, b) => {
        const statusDiff =
          (a.StatusCode === "AC" ? 0 : 1) - (b.StatusCode === "AC" ? 0 : 1);
        if (statusDiff !== 0) return statusDiff;
        return (a.SortOrder ?? 0) - (b.SortOrder ?? 0);
      }),
    [roles],
  );

  useEffect(() => {
    setRoleRows(rows);
  }, [rows]);

  // ── Filtered rows (status filter + search) ─────
  const filteredRoles = useMemo(() => {
    if (!roleRows || roleRows.length === 0) return [];

    return roleRows.filter((role) => {
      const matchesSearch =
        searchTerm === "" ||
        role.RoleName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        role.RoleCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        role.Description?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === "All Status" ||
        (statusFilter === "Active" && role.StatusCode === "AC") ||
        (statusFilter === "Inactive" && role.StatusCode !== "AC");

      return matchesSearch && matchesStatus;
    });
  }, [roleRows, searchTerm, statusFilter]);

  // ── Pagination (recompute totalPages whenever filters/pageSize change,
  //    and clamp currentPage so it can never point past the last page) ─
  const totalPages = Math.max(
    1,
    Math.ceil(filteredRoles.length / itemsPerPage),
  );

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  const paginatedRoles = useMemo(
    () =>
      filteredRoles.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage,
      ),
    [filteredRoles, currentPage, itemsPerPage],
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  // ── Handlers ─────────────────────────────────
  const handleAddRole = useCallback(() => {
    navigate("/role-management/create");
  }, [navigate]);

  const handleEditRole = useCallback(
    (role) => {
      navigate(`/role-management/edit/${role.RoleID}`);
    },
    [navigate],
  );

  const handleDeleteClick = async (role) => {
    const confirmed = await showConfirmDialog(role);
    if (confirmed) {
      try {
        await dispatch(
          deleteRole({
            rID: role.RoleID.toString(),
            user: user?.UserId || user?.EmployeeID || 1,
            crDate: new Date().toISOString(),
          }),
        ).unwrap();

        dispatch(fetchRoles(companyId)).then((res) => {
          const data = res?.payload ?? [];
          setRoles(Array.isArray(data) ? data : []);
          setSuccessMsg("Role deleted successfully.");
          setEditSuccess(true);
          setTimeout(() => setEditSuccess(false), 3000);
        });
      } catch (error) {
        console.error("Failed to delete role:", error);
        setSuccessMsg("Failed to delete role. Please try again.");
        setEditSuccess(true);
        setTimeout(() => setEditSuccess(false), 3000);
      }
    }
  };

  // ── Drag & drop sort order (unchanged behavior) ─────────────
  const saveRoleOrder = useCallback(
    (orderedRows) => {
      setIsSavingOrder(true);
      dispatch(
        role_UpdateSortOrder({
          CompanyID: companyId,
          Items: orderedRows.map((row, i) => ({
            roleID: row.RoleID,
            sortOrder: i + 1,
          })),
        }),
      ).finally(() => setIsSavingOrder(false));
    },
    [dispatch, companyId],
  );

  const handleSortEnd = useCallback(
    (reorderedPage) => {
      const newRows = [...roleRows];
      const pageItemIds = paginatedRoles.map((r) => r.RoleID);
      const fullIndices = pageItemIds.map((id) =>
        newRows.findIndex((r) => r.RoleID === id),
      );
      reorderedPage.forEach((row, i) => {
        newRows[fullIndices[i]] = row;
      });
      setRoleRows(newRows);
      saveRoleOrder(newRows);
    },
    [roleRows, paginatedRoles, saveRoleOrder],
  );

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  }, []);

  const handlePageSizeChange = useCallback((size) => {
    setItemsPerPage(size);
    setCurrentPage(1);
  }, []);

  // ── Columns ───────────────────────────────────
  const columns = useMemo(
    () => [
      {
        key: "RoleCode",
        title: "Role Code",
        render: (row) => (
          <code className="text-[11px] bg-[#08757C]/10 px-2.5 py-1 rounded-lg text-[#08757C] font-mono font-bold">
            {row.RoleCode}
          </code>
        ),
      },
      {
        key: "RoleName",
        title: "Role Name",
        render: (row) => (
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-[#005b60]/10 flex items-center justify-center text-[10px] font-bold text-[#005b60] flex-shrink-0">
              {row.RoleName?.charAt(0)?.toUpperCase()}
            </div>
            <span className="text-sm font-medium text-[#181c1d]">
              {row.RoleName}
            </span>
          </div>
        ),
      },
      {
        key: "Description",
        title: "Description",
        render: (row) => (
          <span className="text-sm text-[#3e494a]">
            {row.Description || "—"}
          </span>
        ),
      },
      {
        key: "StatusCode",
        title: "Status",
        render: (row) => (
          <span
            className={`text-[10px] px-3 py-1 rounded-full font-black uppercase tracking-wider ${
              row.StatusCode === "AC"
                ? "bg-[#bbf46e]/20 text-[#112000]"
                : "bg-red-100 text-red-700"
            }`}
          >
            {row.StatusCode === "AC" ? "Active" : "Inactive"}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <DashboardLayout>
      <div className="px-8 py-4 bg-[#f7fafa] text-[#181c1d] min-h-screen">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-[#3e494a] mb-1.5">
          <span>Administration</span>
          <span className="material-symbols-outlined text-[14px]">
            chevron_right
          </span>
          <span className="text-[#08757C] font-semibold">Role Management</span>
        </nav>

        {/* Header */}
        <div className="flex flex-wrap justify-between items-end mb-2.5 gap-4">
          <PageHeading
            title="Role Management"
            subtitle="Manage system-wide roles and their associated permissions to ensure clinical data integrity and operational security."
          />
          {hasPrivilege(PRIVILEGES.ROLE_CREATE) && (
            <Button variant="primary" onClick={handleAddRole}>
              <Icon name="add" className="mr-1 text-[20px]" />
              Add New Role
            </Button>
          )}
        </div>

        {/* Stats */}
        <StatsRow roles={roles} />

        {/* Status filter above table */}
        <div className="flex items-center gap-3 mb-2.5">
          <label className="text-xs font-bold text-[#3e494a] uppercase tracking-widest">
            Status:
          </label>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="px-3 py-1.5 bg-white border border-[#e5e9e9] rounded-lg text-sm text-[#181c1d] focus:outline-none focus:ring-2 focus:ring-[#08757C]"
          >
            <option value="All Status">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>

        {/* Table (drag-and-drop reordering preserved) */}
        <ConfigTable
          columns={columns}
          rows={loading ? [] : paginatedRoles}
          onEdit={
            hasPrivilege(PRIVILEGES.ROLE_EDIT) ? handleEditRole : undefined
          }
          onDelete={
            hasPrivilege(PRIVILEGES.ROLE_DELETE) ? handleDeleteClick : undefined
          }
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={itemsPerPage}
          onPageChange={setCurrentPage}
          onPageSizeChange={handlePageSizeChange}
          searchQuery={searchTerm}
          onSearchChange={handleSearchChange}
          allFilteredRows={filteredRoles}
          exportColumns={EXPORT_COLUMNS}
          exportFilename="roles"
          emptyText={loading ? "Loading…" : "No roles found."}
          sortable={true}
          onSortEnd={handleSortEnd}
          isSavingOrder={isSavingOrder}
          showPagination={true}
        />
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        title="Delete Role"
        message={(role) =>
          `Are you sure you want to delete "${role?.RoleName}"? This action cannot be undone.`
        }
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonClass="bg-primary hover:bg-primary/90"
        icon="warning"
      />

      {editSuccess && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] bg-[#bbf46e] text-[#112000] px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 text-sm font-semibold"
          role="status"
        >
          <span className="material-symbols-outlined text-[20px]">
            check_circle
          </span>
          {successMsg}
        </div>
      )}
    </DashboardLayout>
  );
};

export default RoleManagementPage;
